import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { AmendmentPlan, Delivery, TourDetail } from '../types';

interface Props {
  tour: TourDetail;
  onClose: () => void;
  /** Tura je izmenjena — spisak i detalj se moraju ponovo učitati. */
  onChanged: () => void;
}

/**
 * Dodavanje tačke turi koja je već puštena ili je vozilo već krenulo.
 *
 * Ekran postoji zbog jednog pitanja koje operater inače ne može da postavi: da
 * li je vozilo tu tačku već prošlo. Odgovor se ne krije i ne blokira — piše
 * koliko je tačka iza vozila i koliko izmena košta, pa operater odlučuje.
 *
 * Razlog je obavezan. Izmena bez razloga se za mesec dana ne može ni oceniti,
 * a upravo se po tome meri da li se dnevne izmene isplate.
 */
export default function AmendmentPanel({ tour, onClose, onChanged }: Props) {
  const [deliveries, setDeliveries] = useState<Delivery[] | null>(null);
  const [chosen, setChosen] = useState<Delivery | null>(null);
  const [plan, setPlan] = useState<AmendmentPlan | null>(null);
  const [reason, setReason] = useState('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    api.deliveries(3)
      .then((rows) => setDeliveries(rows.filter((d) => d.canBePlanned)))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  const check = useCallback(async (delivery: Delivery) => {
    setChosen(delivery);
    setPlan(null);
    setError(null);
    setBusy(true);

    try {
      setPlan(await api.amendmentCheck(tour.tourId, { documentKey: delivery.documentKey }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [tour.tourId]);

  const add = async () => {
    if (!chosen || !plan || plan.blokirano || reason.trim().length === 0) return;

    setBusy(true);
    setError(null);

    try {
      const result = await api.addAmendment(tour.tourId, {
        documentKey: chosen.documentKey,
        reason: reason.trim()
      });

      setDone(`Dodato na ${result.position}. mesto. Tura je sad verzija ${result.version}.`);
      setChosen(null);
      setPlan(null);
      setReason('');
      onChanged();
    } catch (e) {
      // Između pregleda i potvrde vozač ume da stigne na sledeću stanicu, pa
      // se granica ubacivanja pomeri. Tada se provere prikazuju iznova.
      setError(
        'Izmena nije upisana — provere su se u međuvremenu promenile. Pogledaj ponovo.'
      );
      if (chosen) void check(chosen);
    } finally {
      setBusy(false);
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!deliveries) return [];
    if (query.length === 0) return deliveries.slice(0, 40);

    return deliveries
      .filter((d) => `${d.documentKey} ${d.address} ${d.city} ${d.customer}`
        .toLowerCase()
        .includes(query))
      .slice(0, 40);
  }, [deliveries, search]);

  const canAdd = plan !== null && !plan.blokirano && reason.trim().length > 0 && !busy;

  return (
    <section className="panel amend">
      <div className="tourdetail__head">
        <h3>Dodaj tačku · {tour.name}</h3>
        <span className="tourdetail__actions">
          <button type="button" className="link" onClick={onClose}>Zatvori</button>
        </span>
      </div>

      <p className="muted">
        Stanica na kojoj je vozač već bio se ne dira — nova tačka ulazi iza nje,
        i kad geografski leži unazad.
      </p>

      {error && <p className="blocker">{error}</p>}
      {done && <p className="health health--ok">{done}</p>}

      <input
        type="search"
        value={search}
        placeholder="Traži po adresi, mestu, kupcu ili broju otpremnice"
        onChange={(e) => setSearch(e.target.value)}
      />

      {deliveries === null ? (
        <p className="muted">Učitavam isporuke…</p>
      ) : filtered.length === 0 ? (
        <p className="empty">Nema isporuke koja odgovara toj pretrazi.</p>
      ) : (
        <ul className="amend__list">
          {filtered.map((delivery) => (
            <li key={delivery.documentKey}>
              <button
                type="button"
                className={chosen?.documentKey === delivery.documentKey
                  ? 'amend__pick amend__pick--on'
                  : 'amend__pick'}
                onClick={() => void check(delivery)}
              >
                <span className="amend__where">
                  {delivery.address}, {delivery.city}
                </span>
                <span className="muted">
                  {delivery.documentKey} · čeka {delivery.waitingDays} d
                  {delivery.requiresTwoPeople && ' · dva čoveka'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {busy && !plan && <p className="muted">Proveravam…</p>}

      {plan && chosen && (
        <div className="amend__plan">
          <h4>{plan.stanica || `${chosen.address}, ${chosen.city}`}</h4>

          <p className="amend__cost">
            {plan.mesto}. mesto · +{plan.dodatoKm.toFixed(1)} km · +{Math.round(plan.dodatoMin)} min
            {plan.izaVozilaKm !== null && ` · ${plan.izaVozilaKm.toFixed(1)} km iza vozila`}
          </p>

          <ul className="amend__checks">
            {plan.provere.map((check_, i) => (
              <li
                key={`${check_.vrsta}-${i}`}
                className={check_.nivo === 'blokira'
                  ? 'amend__check amend__check--stop'
                  : check_.nivo === 'upozorenje'
                    ? 'amend__check amend__check--warn'
                    : 'amend__check'}
              >
                {check_.poruka}
              </li>
            ))}
          </ul>

          {plan.blokirano ? (
            <p className="blocker">
              Ovo se ne može dodati dok gornje ne bude rešeno.
            </p>
          ) : (
            <>
              <label className="amend__reason">
                Zašto se dodaje
                <input
                  type="text"
                  value={reason}
                  placeholder="npr. kupac zvao, roba mu treba danas"
                  onChange={(e) => setReason(e.target.value)}
                />
              </label>

              <button type="button" className="primary" disabled={!canAdd} onClick={() => void add()}>
                Dodaj na turu
              </button>

              {plan.provere.some((c) => c.nivo === 'upozorenje') && (
                <p className="note">
                  Upozorenja se ne gube: zapisuju se uz izmenu, onako kako ih sad vidiš.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
